const SHEET_SELECTOR = '.evw-print';
const SHARE_FLYER_SELECTOR = '[data-share-flyer]';
const LOADING_CLASS = 'is-downloading';
const LETTER_MM = { width: 215.9, height: 279.4 };
const FOOTER_STRIP_MM = 10.16;
// Page 1 bleeds to the paper edge; the pages after it keep ordinary margins,
// matching @page evw-sheet and @page evw-content.
const FLYER_MM = { x: 0, y: 0, width: LETTER_MM.width, height: LETTER_MM.height };
const PAGE_MARGIN_MM = { x: 21.59, y: 17.78 };
const CONTENT_MM = {
  x: PAGE_MARGIN_MM.x,
  y: PAGE_MARGIN_MM.y,
  width: LETTER_MM.width - PAGE_MARGIN_MM.x * 2,
  height: LETTER_MM.height - PAGE_MARGIN_MM.y - FOOTER_STRIP_MM
};
const SHEET_WIDTH = { flyer: '8.5in', content: '6.8in' };
const MAX_CANVAS_PIXELS = 16000000;
const TRANSPARENT_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
const PREFERRED_PIXEL_RATIO = 2;

const FLYER_SELECTOR = '.evw-print__flyer';
const DESCRIPTION_SECTION_SELECTOR = '.evw-print__flyer .evw-print__section';
const REGISTER_SELECTOR = '.evw-print__flyer .evw-print__register';
const MAX_REGISTER_PASSES = 4;
const TRUNCATED_CLASS = 'is-truncated';
const MAX_TRIM_STEPS = 40;
const PAGES_SELECTOR = '.evw-print__pages';
const FOOTER_COLOR = [152, 162, 179];
const FOOTER_FONT_SIZE = 8;

const UNSPLITTABLE_SELECTOR = [
  '.evw-print__page-title',
  '.evw-print__about tr',
  '.evw-print__about li',
  '.evw-print__about img'
].join(', ');

const ABOUT_SELECTOR = '.evw-print__about';
const BLOCK_DISPLAYS = ['block', 'list-item', 'flex', 'grid', 'table'];

const sheetPdfPromises = new WeakMap();

const isBlockElement = (node) =>
  node.nodeType === window.Node.ELEMENT_NODE && BLOCK_DISPLAYS.includes(window.getComputedStyle(node).display);

const hasVisibleContent = (nodes) =>
  nodes.some((node) => node.nodeType === window.Node.ELEMENT_NODE || node.textContent.trim() !== '');

const wrapLooseInlineContent = (element) => {
  const children = Array.from(element.childNodes);
  if (!children.some(isBlockElement)) return;

  let run = [];
  const flush = (beforeNode) => {
    if (hasVisibleContent(run)) {
      const wrapper = document.createElement('div');
      element.insertBefore(wrapper, beforeNode);
      run.forEach((node) => wrapper.appendChild(node));
    }
    run = [];
  };

  children.forEach((node) => {
    if (isBlockElement(node)) {
      flush(node);
      wrapLooseInlineContent(node);
    } else {
      run.push(node);
    }
  });
  flush(null);
};

const aboutLeafBlocks = (copy) =>
  Array.from(copy.querySelectorAll(ABOUT_SELECTOR)).flatMap((about) => {
    wrapLooseInlineContent(about);
    return Array.from(about.querySelectorAll('*')).filter(
      (element) => isBlockElement(element) && !Array.from(element.children).some(isBlockElement)
    );
  });

const KEEP_WITH_NEXT_SELECTOR = [
  '.evw-print__page-title',
  '.evw-print__about h1',
  '.evw-print__about h2',
  '.evw-print__about h3',
  '.evw-print__about h4',
  '.evw-print__about h5',
  '.evw-print__about h6'
].join(', ');

// A canvas cannot read back an image whose host does not allow it, and most
// hosts do not. The sheet carries a second address for those images, served by
// us, and the copy that becomes the PDF is the only place it is used.
const swapInProxiedImages = (root) => {
  Array.from(root.querySelectorAll('img[data-print-src]')).forEach((image) => {
    // Only the address changes: it is our own origin, so the canvas can read it
    // and no `crossorigin` is needed. Setting that attribute first would refetch
    // the organizer's address under CORS and fail there for nothing.
    image.setAttribute('src', image.getAttribute('data-print-src'));
  });
};

const mountOffscreenCopy = (sheet, sectionSelector) => {
  const holder = document.createElement('div');
  Object.assign(holder.style, { position: 'fixed', left: '-10000px', top: '0', background: '#fff' });
  const copy = sheet.cloneNode(true);
  Array.from(copy.children).forEach((child) => {
    if (!child.matches(sectionSelector)) child.remove();
  });
  const width = sectionSelector === FLYER_SELECTOR ? SHEET_WIDTH.flyer : SHEET_WIDTH.content;
  Object.assign(copy.style, { display: 'block', minHeight: '0', width });
  holder.appendChild(copy);
  document.body.appendChild(holder);
  return { holder, copy };
};

const waitForImages = (root) =>
  Promise.all(
    Array.from(root.querySelectorAll('img')).map((img) =>
      img.complete ? Promise.resolve() : new Promise((resolve) => {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
      })
    )
  );

const measureBlocks = (copy) => {
  const aboutBlocks = aboutLeafBlocks(copy);
  const origin = copy.getBoundingClientRect().top;
  const blocks = Array.from(copy.querySelectorAll(UNSPLITTABLE_SELECTOR)).concat(aboutBlocks)
    .map((element) => {
      const rect = element.getBoundingClientRect();
      return { top: rect.top - origin, bottom: rect.bottom - origin, keepWithNext: element.matches(KEEP_WITH_NEXT_SELECTOR) };
    })
    .sort((a, b) => a.top - b.top);

  return blocks.map((block, index) => {
    const next = blocks.slice(index + 1).find((candidate) => candidate.top >= block.bottom - 1);
    return block.keepWithNext && next ? { ...block, bottom: Math.max(block.bottom, next.bottom) } : block;
  });
};

const RENDER_SLACK_RATIO = 0.05;
const BREAK_SEARCH_RATIO = 0.25;
const MIN_GAP_CSS_PX = 1;
const BLANK_CHANNEL_MIN = 250;
const LIGHT_CHANNEL_MIN = 200;
const ROW_BLANK = 2;
const ROW_LIGHT = 1;
const IS_SAFARI = /^((?!chrome|android|crios|fxios|edg).)*safari/i.test(window.navigator.userAgent);

const rowLevelsOf = (canvas) => {
  const { data } = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height);
  const rows = new Uint8Array(canvas.height);
  for (let y = 0; y < canvas.height; y += 1) {
    let level = ROW_BLANK;
    for (let i = y * canvas.width * 4, end = i + canvas.width * 4; i < end && level > 0; i += 4) {
      const darkest = Math.min(data[i], data[i + 1], data[i + 2]);
      if (darkest < LIGHT_CHANNEL_MIN) level = 0;
      else if (darkest < BLANK_CHANNEL_MIN) level = ROW_LIGHT;
    }
    rows[y] = level;
  }
  return rows;
};

const contentBottomOf = (rows) => {
  let bottom = rows.length;
  while (bottom > 0 && rows[bottom - 1] === ROW_BLANK) bottom -= 1;
  return bottom;
};

const pageBottomFor = (blocks, pageTop, limit) => {
  const crossing = blocks.filter((block) => block.top < limit && block.bottom > limit && block.top > pageTop);
  return crossing.length ? Math.max(...crossing.map((block) => block.top)) : limit;
};

const findGap = (rows, pageTop, limit, minGap, level) => {
  const floor = Math.max(pageTop + 1, Math.round(limit - (limit - pageTop) * BREAK_SEARCH_RATIO));
  let run = 0;
  for (let y = limit - 1; y >= floor; y -= 1) {
    run = rows[y] >= level ? run + 1 : 0;
    if (run >= minGap) return y + Math.floor(minGap / 2);
  }
  return null;
};

const snapToGap = (rows, pageTop, limit, minGap) => {
  const blankGap = findGap(rows, pageTop, limit, minGap, ROW_BLANK);
  if (blankGap !== null) return blankGap;
  const lightGap = findGap(rows, pageTop, limit, minGap, ROW_LIGHT);
  return lightGap !== null ? lightGap : limit;
};

const skipBlankRows = (rows, from, totalHeight) => {
  let top = from;
  while (top < totalHeight && rows[top] === ROW_BLANK) top += 1;
  return top;
};

const pageBreaks = (blocks, rows, totalHeight, pageHeight, minGap) => {
  const pages = [];
  let pageTop = 0;

  while (pageTop < totalHeight) {
    const limit = pageTop + pageHeight;
    if (limit >= totalHeight) {
      pages.push([pageTop, totalHeight]);
      break;
    }

    const preferred = Math.round(pageBottomFor(blocks, pageTop, limit));
    const pageBottom = snapToGap(rows, pageTop, preferred, minGap);
    pages.push([pageTop, pageBottom]);
    pageTop = skipBlankRows(rows, pageBottom, totalHeight);
  }

  return pages;
};

const renderSheet = async (toCanvas, node, options) => {
  if (IS_SAFARI) await toCanvas(node, options);
  return toCanvas(node, options);
};

const lineHeightOf = (element) => {
  const style = window.getComputedStyle(element);
  const lineHeight = parseFloat(style.lineHeight);
  return Number.isFinite(lineHeight) ? lineHeight : parseFloat(style.fontSize) * 1.4;
};

const clampToLines = (element, lines) => {
  Object.assign(element.style, {
    display: '-webkit-box', overflow: 'hidden', WebkitBoxOrient: 'vertical', WebkitLineClamp: String(lines)
  });
  element.setAttribute('data-clamped-lines', String(lines));
};

// Keeps what fits above `limit` and drops the rest. A paragraph crossing the
// limit is cut on a whole line; a list item crossing it is dropped, since a
// clamped item would lose its bullet.
const cutBlocksAt = (container, limit) => {
  Array.from(container.children).forEach((block) => {
    const rect = block.getBoundingClientRect();
    if (rect.top >= limit) {
      block.remove();
    } else if (rect.bottom > limit) {
      if (block.matches('ul, ol')) {
        cutBlocksAt(block, limit);
        if (block.children.length === 0) block.remove();
      } else if (block.matches('li')) {
        block.remove();
      } else {
        const lines = Math.floor((limit - rect.top) / lineHeightOf(block));
        if (lines > 0) clampToLines(block, lines);
        else block.remove();
      }
    }
  });
};

const lastBlockOf = (container) => {
  const last = container.lastElementChild;
  return last && last.matches('ul, ol') && last.lastElementChild ? last.lastElementChild : last;
};

// Margins can leave the cut a few pixels long, so step back a line at a time
// until the section really fits.
const trimUntilFits = (section, description) => {
  for (let step = 0; step < MAX_TRIM_STEPS && section.scrollHeight > section.clientHeight + 1; step += 1) {
    const block = lastBlockOf(description);
    if (!block) return;
    const lines = Number(block.dataset.clampedLines || Math.round(block.offsetHeight / lineHeightOf(block)));
    if (lines > 1 && !block.matches('li')) {
      clampToLines(block, lines - 1);
    } else {
      const parent = block.parentElement;
      block.remove();
      if (parent !== description && parent.children.length === 0) parent.remove();
    }
  }
};

// The flyer's description is cut where page 1 runs out of room, on a whole line,
// with "Read more online" after it. Needs the sheet laid out at flyer width.
// Print cuts the page's own sheet, and a Download afterwards copies it already
// cut, so a cut description is cut again if the room has shrunk since.
const fitFlyerDescription = (root) => {
  const section = root.querySelector(DESCRIPTION_SECTION_SELECTOR);
  if (!section) return;
  if (section.scrollHeight <= section.clientHeight + 1) return;

  const description = section.querySelector('.evw-print__description');
  const readMore = section.querySelector('.evw-print__read-more');
  if (!description || !readMore) return;

  section.classList.add(TRUNCATED_CLASS);
  const gap = parseFloat(window.getComputedStyle(section).rowGap) || 0;
  const limit = section.getBoundingClientRect().top + section.clientHeight - readMore.offsetHeight - gap;
  cutBlocksAt(description, limit);
  trimUntilFits(section, description);
};

// The sheet is hidden on screen, and a hidden element has no size to measure,
// so it is laid out off screen at flyer width for as long as `work` runs.
const withSheetLaidOut = (sheet, work) => {
  const previousStyle = sheet.getAttribute('style');
  Object.assign(sheet.style, {
    display: 'block', position: 'fixed', left: '-10000px', top: '0', width: SHEET_WIDTH.flyer
  });
  const restore = () => {
    if (previousStyle === null) sheet.removeAttribute('style');
    else sheet.setAttribute('style', previousStyle);
  };
  let result;
  try {
    result = work();
  } catch (error) {
    restore();
    throw error;
  }
  if (result && typeof result.then === 'function') return result.finally(restore);

  // Restored before returning, not in a promise callback: `beforeprint` runs this
  // synchronously and the browser takes the print snapshot right after, so a
  // deferred restore printed the sheet while it was still parked off screen.
  restore();
  return Promise.resolve(result);
};

// The register card is a size container (that is how it knows when to stack),
// so its text never makes the card taller, and long dates ran into the link box
// below them. The row floor grows by what the text is missing and the
// description gives that room back. One extra line covers Print, which sets the
// text a little wider than the screen layout measured here. A taller row also
// makes the QR code wider and the text beside it narrower, so the check runs
// again until it holds.
const makeRoomForRegisterText = (root) => {
  const register = root.querySelector(REGISTER_SELECTOR);
  const columns = register && register.closest('.evw-print__columns');
  const text = register && register.querySelector('.evw-print__register-text');
  if (!columns || !text || !text.lastElementChild) return;

  const card = register.closest('.evw-print__card');
  const slack = lineHeightOf(text.lastElementChild);
  for (let pass = 0; pass < MAX_REGISTER_PASSES; pass += 1) {
    const shortfall = text.offsetHeight + slack - register.clientHeight;
    if (shortfall <= 0) return;
    columns.style.gridAutoRows = `minmax(${Math.ceil(card.offsetHeight + shortfall)}px, 1fr)`;
  }
};

const fitFlyer = (root) => {
  makeRoomForRegisterText(root);
  fitFlyerDescription(root);
};

const fitSheetForPrint = (waitForSheetImages) => {
  const sheet = document.querySelector(SHEET_SELECTOR);
  if (!sheet) return Promise.resolve();
  return withSheetLaidOut(sheet, () => (waitForSheetImages
    ? waitForImages(sheet).then(() => fitFlyer(sheet))
    : fitFlyer(sheet)));
};

// Printing from the browser's own menu skips the Print button, so the cut also
// runs here. It can't wait for images, which the page has loaded by then anyway.
const fitSheetBeforePrint = () => {
  fitSheetForPrint(false);
};

// The stylesheet sets the responses sentence in lower case and raises its first
// letter with ::first-letter. html-to-image copies computed styles but not that
// pseudo-element, so in the PDF copy the casing is written into the text itself.
const SENTENCE_CASE_SELECTOR = '.evw-print__register-text .evw-print__muted:last-child';

const bakeSentenceCase = (copy) => {
  copy.querySelectorAll(SENTENCE_CASE_SELECTOR).forEach((element) => {
    const text = element.textContent.trim().toLowerCase();
    element.replaceChildren(document.createTextNode(text.charAt(0).toUpperCase() + text.slice(1)));
    element.style.setProperty('text-transform', 'none');
  });
};

const captureSection = async (toCanvas, sheet, sectionSelector) => {
  if (!sheet.querySelector(sectionSelector)) return null;
  const { holder, copy } = mountOffscreenCopy(sheet, sectionSelector);

  try {
    swapInProxiedImages(copy);
    await waitForImages(copy);
    if (sectionSelector === FLYER_SELECTOR) {
      bakeSentenceCase(copy);
      fitFlyer(copy);
    }
    const width = copy.offsetWidth;
    const renderHeight = Math.ceil(copy.offsetHeight * (1 + RENDER_SLACK_RATIO));
    const pixelRatio = Math.min(PREFERRED_PIXEL_RATIO, Math.sqrt(MAX_CANVAS_PIXELS / (width * renderHeight)));
    const measuredBlocks = measureBlocks(copy);
    const canvas = await renderSheet(toCanvas, copy, {
      pixelRatio,
      height: renderHeight,
      // No cacheBust: it appends a parameter to every image URL, and an
      // ImageKit URL already carries its transformation query, so the extra
      // one turned the request into a 404 and dropped the image.
      backgroundColor: '#ffffff',
      // An image the organizer linked can be gone. Without a placeholder the
      // whole PDF fails on it, so a missing one leaves a gap instead.
      imagePlaceholder: TRANSPARENT_PIXEL,
      skipFonts: true
    });

    const scale = canvas.width / width;
    const rows = rowLevelsOf(canvas);
    return {
      canvas,
      rows,
      scale,
      totalHeight: contentBottomOf(rows),
      blocks: measuredBlocks.map((block) => ({ top: block.top * scale, bottom: block.bottom * scale }))
    };
  } finally {
    holder.remove();
  }
};

const addSlice = (pdf, canvas, top, bottom, xMm, yMm, widthMm) => {
  const slice = document.createElement('canvas');
  slice.width = canvas.width;
  slice.height = Math.max(1, bottom - top);
  slice.getContext('2d').drawImage(canvas, 0, top, slice.width, slice.height, 0, 0, slice.width, slice.height);
  const heightMm = (slice.height / canvas.width) * widthMm;
  pdf.addImage(slice.toDataURL('image/png'), 'PNG', xMm, yMm, widthMm, heightMm, undefined, 'FAST');
};

const addFlyerPage = (pdf, flyer) => {
  const heightMm = (flyer.totalHeight / flyer.canvas.width) * FLYER_MM.width;
  const widthMm = FLYER_MM.width * Math.min(1, FLYER_MM.height / heightMm);
  addSlice(pdf, flyer.canvas, 0, flyer.totalHeight, (FLYER_MM.width - widthMm) / 2, FLYER_MM.y, widthMm);
};

const addContentPages = (pdf, content) => {
  const pageHeight = Math.floor((CONTENT_MM.height / CONTENT_MM.width) * content.canvas.width);
  const minGap = Math.ceil(MIN_GAP_CSS_PX * content.scale);
  pageBreaks(content.blocks, content.rows, content.totalHeight, pageHeight, minGap).forEach(([top, bottom]) => {
    pdf.addPage('letter', 'portrait');
    addSlice(pdf, content.canvas, top, bottom, CONTENT_MM.x, CONTENT_MM.y, CONTENT_MM.width);
  });
};

const writeFooters = (pdf, sheet) => {
  const total = pdf.getNumberOfPages();
  const y = LETTER_MM.height - FOOTER_STRIP_MM / 2;
  const right = LETTER_MM.width - CONTENT_MM.x;
  const startWidth = CONTENT_MM.width * 0.6;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(FOOTER_FONT_SIZE);
  pdf.setTextColor(...FOOTER_COLOR);

  // Page 1 is the flyer and carries no footer.
  for (let page = 2; page <= total; page += 1) {
    pdf.setPage(page);
    const start = sheet.dataset.footerName || '';
    const end = (sheet.dataset.footerPage || '').replace('{pages}', String(total)).replace('{page}', String(page));
    pdf.text(pdf.splitTextToSize(start, startWidth)[0] || '', CONTENT_MM.x, y);
    pdf.text(end, right, y, { align: 'right' });
  }
};

const buildSheetPdf = async (sheet) => {
  const [{ toCanvas }, { jsPDF: JsPDF }] = await Promise.all([import('html-to-image'), import('jspdf')]);
  const flyer = await captureSection(toCanvas, sheet, FLYER_SELECTOR);
  const content = await captureSection(toCanvas, sheet, PAGES_SELECTOR);
  const pdf = new JsPDF({ unit: 'mm', format: 'letter', orientation: 'portrait' });

  if (flyer) addFlyerPage(pdf, flyer);
  if (content) addContentPages(pdf, content);
  writeFooters(pdf, sheet);
  return pdf;
};

const sheetPdf = () => {
  const sheet = document.querySelector(SHEET_SELECTOR);
  if (!sheet) return Promise.resolve(null);

  if (!sheetPdfPromises.has(sheet)) {
    sheetPdfPromises.set(sheet, buildSheetPdf(sheet).catch((error) => {
      sheetPdfPromises.delete(sheet);
      throw error;
    }));
  }
  return sheetPdfPromises.get(sheet);
};

export const canShareFiles = () =>
  typeof window.navigator.share === 'function'
  && typeof window.navigator.canShare === 'function'
  && window.navigator.canShare({ files: [new window.File([''], 'sheet.pdf', { type: 'application/pdf' })] });

const ACTION_SHEET_SELECTOR = '[data-evw-menu]';
const ACTION_SHEET_OPEN_CLASS = 'evw-action-sheet-open';
const ACTION_SHEET_TRIGGER_SELECTOR = `${ACTION_SHEET_SELECTOR} > .dropdown__holder > .dropdown__button`;
const MENU_ITEM_SELECTOR = '.evw-actions__menu [role="menuitem"]';

const visibleMenuItems = (actionSheet) =>
  Array.from(actionSheet.querySelectorAll(MENU_ITEM_SELECTOR)).filter((item) => item.getClientRects().length > 0);

const focusMenuItem = (actionSheet, index) => {
  const items = visibleMenuItems(actionSheet);
  if (items.length) items[(index + items.length) % items.length].focus();
};

const TABBABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(', ');

const tabPastActionSheet = (actionSheet, backwards) => {
  const menu = actionSheet.querySelector('.evw-actions__menu');
  const trigger = actionSheet.querySelector('.dropdown__button');
  const tabbables = Array.from(document.querySelectorAll(TABBABLE_SELECTOR)).filter((element) =>
    !menu.contains(element)
    && element.getClientRects().length > 0
    && window.getComputedStyle(element).visibility !== 'hidden');
  const index = tabbables.indexOf(trigger);
  const target = backwards ? trigger : tabbables[index + 1];
  (target || trigger).focus();
};

const focusTrigger = (actionSheet) => {
  const trigger = actionSheet.querySelector('.dropdown__button');
  if (trigger) trigger.focus();
};

const setActionSheetOpen = (actionSheet, open) => {
  actionSheet.classList.toggle('is-active', open);
  const trigger = actionSheet.querySelector('.dropdown__button');
  if (trigger) trigger.setAttribute('aria-expanded', String(open));
  document.body.classList.toggle(ACTION_SHEET_OPEN_CLASS, !!document.querySelector(`${ACTION_SHEET_SELECTOR}.is-active`));
};

const closeActionSheet = (element) => {
  const actionSheet = element.closest(ACTION_SHEET_SELECTOR);
  if (actionSheet) setActionSheetOpen(actionSheet, false);
};

const closeOpenActionSheets = (exceptTarget) => {
  document.querySelectorAll(`${ACTION_SHEET_SELECTOR}.is-active`).forEach((actionSheet) => {
    if (!exceptTarget || !actionSheet.contains(exceptTarget)) setActionSheetOpen(actionSheet, false);
  });
};

const loadingIndicatorFor = (button) => {
  const actionSheet = button.closest(ACTION_SHEET_SELECTOR);
  return actionSheet ? actionSheet.querySelector('.dropdown__button') : button;
};

const withLoading = async (button, work) => {
  const indicator = loadingIndicatorFor(button);
  if (indicator.classList.contains(LOADING_CLASS)) return;

  const label = button.querySelector('.dropdown__legend');
  const idleLabel = label ? label.textContent : '';
  const loadingLabel = button.getAttribute('data-loading-label');
  indicator.classList.add(LOADING_CLASS);
  indicator.setAttribute('aria-busy', 'true');
  button.blur();
  if (label && loadingLabel) label.textContent = loadingLabel;

  try {
    await work();
  } finally {
    indicator.classList.remove(LOADING_CLASS);
    indicator.removeAttribute('aria-busy');
    if (label) label.textContent = idleLabel;
  }
};

const downloadSheet = async (fileName) => {
  const pdf = await sheetPdf();
  if (pdf) pdf.save(fileName);
};

const sheetShareData = async (button) => {
  const title = button.getAttribute('data-share-title');
  const url = button.getAttribute('data-share-url');
  // No `title` here, unlike sharing the page: with a file attached, Notes keeps
  // only the body while Messages shows both fields, so sending the two printed
  // the event name twice there. The body alone reads the same everywhere.
  const shareData = { text: `${title}\n${url}` };

  try {
    const pdf = await sheetPdf();
    if (pdf) {
      const file = new window.File([pdf.output('blob')], button.getAttribute('data-share-flyer'), { type: 'application/pdf' });
      if (window.navigator.canShare({ files: [file] })) shareData.files = [file];
    }
  } catch (error) {
    shareData.files = undefined;
  }

  return shareData;
};

const shareSheet = async (button) => {
  let shareData = null;
  await withLoading(button, async () => {
    shareData = await sheetShareData(button);
  });
  if (shareData) window.navigator.share(shareData).catch(() => {});
};

// An inset that fails to load is dropped rather than left as an empty white
// frame over the cover, both on the printed page and in the PDF copy.
const dropBrokenInsets = () => {
  document.querySelectorAll('.evw-print__banner-inset').forEach((img) => {
    if (img.complete && img.naturalWidth === 0) {
      img.remove();
    } else {
      img.addEventListener('error', () => img.remove(), { once: true });
    }
  });
};

const EventPrint = {
  start() {
    dropBrokenInsets();
    // Sharing a file is not something every browser can do, and a menu item that
    // silently does nothing is worse than one that isn't there.
    if (!canShareFiles()) {
      document.querySelectorAll(SHARE_FLYER_SELECTOR).forEach((button) => {
        const item = button.closest('li');
        if (item) item.remove();
      });
    }
    window.removeEventListener('beforeprint', fitSheetBeforePrint);
    window.addEventListener('beforeprint', fitSheetBeforePrint);
    $(document)
      .off('click.eventPrint')
      .on('click.eventPrint', '[data-event-print]', (event) => {
        event.preventDefault();
        closeActionSheet(event.currentTarget);
        fitSheetForPrint(true).finally(() => window.print());
      })
      .on('click.eventPrint', '[data-event-download]', (event) => {
        event.preventDefault();
        const button = event.currentTarget;
        if (loadingIndicatorFor(button).classList.contains(LOADING_CLASS)) return;
        withLoading(button, () => downloadSheet(button.getAttribute('data-event-download')))
          .finally(() => closeActionSheet(button));
      })
      .on('click.eventPrint', '[data-action-sheet-close]', (event) => {
        event.preventDefault();
        const actionSheet = event.currentTarget.closest(ACTION_SHEET_SELECTOR);
        setActionSheetOpen(actionSheet, false);
        focusTrigger(actionSheet);
      })
      .on('click.eventPrint', ACTION_SHEET_TRIGGER_SELECTOR, (event) => {
        event.preventDefault();
        const actionSheet = event.currentTarget.closest(ACTION_SHEET_SELECTOR);
        const open = !actionSheet.classList.contains('is-active');
        setActionSheetOpen(actionSheet, open);
        if (open && event.originalEvent && event.originalEvent.detail === 0) focusMenuItem(actionSheet, 0);
      })
      .on('keydown.eventPrint', ACTION_SHEET_TRIGGER_SELECTOR, (event) => {
        const firstIndexByKey = { ' ': 0, ArrowDown: 0, ArrowUp: -1 };
        if (!(event.key in firstIndexByKey)) return;
        event.preventDefault();
        const actionSheet = event.currentTarget.closest(ACTION_SHEET_SELECTOR);
        setActionSheetOpen(actionSheet, true);
        focusMenuItem(actionSheet, firstIndexByKey[event.key]);
      })
      .on('keydown.eventPrint', MENU_ITEM_SELECTOR, (event) => {
        const actionSheet = event.currentTarget.closest(ACTION_SHEET_SELECTOR);
        if (event.key === 'Tab') {
          event.preventDefault();
          setActionSheetOpen(actionSheet, false);
          tabPastActionSheet(actionSheet, event.shiftKey);
          return;
        }
        const index = visibleMenuItems(actionSheet).indexOf(event.currentTarget);
        const nextIndexByKey = { ArrowDown: index + 1, ArrowUp: index - 1, Home: 0, End: -1 };
        if (!(event.key in nextIndexByKey)) return;
        event.preventDefault();
        focusMenuItem(actionSheet, nextIndexByKey[event.key]);
      })
      .on('click.eventPrint', ACTION_SHEET_SELECTOR, (event) => {
        if (event.target === event.currentTarget) closeActionSheet(event.currentTarget);
      })
      .on('click.eventPrint', (event) => {
        closeOpenActionSheets(event.target);
      })
      .on('keydown.eventPrint', (event) => {
        if (event.key !== 'Escape') return;
        const focusedActionSheet = document.activeElement && document.activeElement.closest(`${ACTION_SHEET_SELECTOR}.is-active`);
        closeOpenActionSheets(null);
        if (focusedActionSheet) focusTrigger(focusedActionSheet);
      })
      .on('click.eventPrint', SHARE_FLYER_SELECTOR, (event) => {
        event.preventDefault();
        const button = event.currentTarget;
        shareSheet(button).finally(() => closeActionSheet(button));
      });
  }
};

export default EventPrint;
